'use client'

import '@assistant-ui/react-markdown/styles/dot.css'

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from '@assistant-ui/react-markdown'
import remarkGfm from 'remark-gfm'
import { type FC, memo, useState } from 'react'
import { CheckIcon, CopyIcon } from 'lucide-react'

import { TooltipIconButton } from '@/components/assistant-ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { MermaidDiagram } from './mermaid-diagram'
import { SyntaxHighlighter } from './shiki-highlighter.aui'

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md min-w-0 max-w-full"
      components={defaultComponents}
      componentsByLanguage={{
        mermaid: {
          SyntaxHighlighter: MermaidDiagram,
        },
      }}
      defer
    />
  )
}

export const MarkdownText = memo(MarkdownTextImpl)

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard()
  const onCopy = () => {
    if (!code || isCopied) return
    copyToClipboard(code)
  }

  return (
    <div className="aui-code-header-root mt-3 flex items-center justify-between rounded-t-xl border border-b-0 border-border/50 bg-muted/50 px-3.5 py-1.5 text-xs">
      <span className="aui-code-header-language font-medium lowercase text-muted-foreground">{language}</span>
      <TooltipIconButton tooltip="Copy" onClick={onCopy}>
        {!isCopied && <CopyIcon className="duration-150 animate-in fade-in zoom-in-75" />}
        {isCopied && <CheckIcon className="duration-200 ease-out animate-in fade-in zoom-in-50" />}
      </TooltipIconButton>
    </div>
  )
}

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false)

  const copyToClipboard = (value: string) => {
    if (!value || typeof navigator === 'undefined' || !navigator.clipboard) {
      return
    }

    navigator.clipboard.writeText(value).then(
      () => {
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), copiedDuration)
      },
      () => {}
    )
  }

  return { isCopied, copyToClipboard }
}

const defaultComponents = memoizeMarkdownComponents({
  SyntaxHighlighter,
  h1: ({ className, ...props }) => (
    <h1
      className={cn('aui-md-h1 mb-2 mt-5 scroll-m-20 text-xl font-semibold first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn('aui-md-h2 mb-2 mt-5 scroll-m-20 text-lg font-semibold first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn('aui-md-h3 mb-1.5 mt-4 scroll-m-20 text-base font-semibold first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn('aui-md-h4 mb-1 mt-3.5 scroll-m-20 text-base font-medium first:mt-0 last:mb-0', className)}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5 className={cn('aui-md-h5 mb-1 mt-3 text-sm font-semibold first:mt-0 last:mb-0', className)} {...props} />
  ),
  h6: ({ className, ...props }) => (
    <h6 className={cn('aui-md-h6 mb-1 mt-3 text-sm font-medium first:mt-0 last:mb-0', className)} {...props} />
  ),
  p: ({ className, ...props }) => (
    <p className={cn('aui-md-p my-3 leading-relaxed first:mt-0 last:mb-0', className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn('aui-md-a text-primary underline underline-offset-2 hover:text-primary/80', className)}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        'aui-md-blockquote my-3 border-s-2 border-muted-foreground/30 ps-4 text-muted-foreground',
        className
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn('aui-md-ul my-3 ms-5 list-disc marker:text-muted-foreground [&>li]:mt-1', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn('aui-md-ol my-3 ms-5 list-decimal marker:text-muted-foreground [&>li]:mt-1', className)}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn('aui-md-hr my-3 border-muted-foreground/20', className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <div className="my-3 max-w-full overflow-x-auto">
      <table className={cn('aui-md-table w-full min-w-max border-separate border-spacing-0', className)} {...props} />
    </div>
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        'aui-md-th [[align=center]]:text-center [[align=right]]:text-right bg-muted px-3 py-1.5 text-start font-medium first:rounded-ss-lg last:rounded-se-lg',
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        'aui-md-td [[align=center]]:text-center [[align=right]]:text-right border-b border-s border-muted-foreground/20 px-3 py-1.5 text-start last:border-e',
        className
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        'aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-es-lg [&:last-child>td:last-child]:rounded-ee-lg',
        className
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => <li className={cn('aui-md-li leading-relaxed', className)} {...props} />,
  strong: ({ className, ...props }) => <strong className={cn('aui-md-strong font-semibold', className)} {...props} />,
  sup: ({ className, ...props }) => (
    <sup className={cn('aui-md-sup [&>a]:text-xs [&>a]:no-underline', className)} {...props} />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={cn(
        'aui-md-pre block min-w-0 max-w-full overflow-x-auto rounded-b-xl rounded-t-none border border-t-0 border-border/50 bg-muted/30 p-3.5 text-[13px] leading-relaxed',
        className
      )}
      {...props}
    />
  ),
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock()
    return (
      <code
        className={cn(
          !isCodeBlock && 'aui-md-inline-code rounded-md bg-muted px-1.5 py-0.5 font-mono text-[0.85em]',
          className
        )}
        {...props}
      />
    )
  },
  CodeHeader,
})
